import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErrorMessage, photoUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, MapPin, Building2, Calendar, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FALLBACK_PROP = "https://images.unsplash.com/photo-1776619316276-b1b461af9f15?crop=entropy&cs=srgb&fm=jpg&w=800";
const FALLBACK_PROJ = "https://images.unsplash.com/photo-1523217582562-09d0def993a6?crop=entropy&cs=srgb&fm=jpg&w=800";

export default function Dashboard() {
  const { t, formatPrice } = useLocale();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "projects" ? "projects" : "properties";

  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [propsRes, projsRes] = await Promise.all([
        api.get("/properties/mine"),
        api.get("/projects/mine"),
      ]);
      setProperties(propsRes.data);
      setProjects(projsRes.data);
    } catch {
      setProperties([]); setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const delProperty = async (property_id) => {
    try {
      await api.delete(`/properties/${property_id}`);
      toast.success("Propiedad eliminada");
      setProperties((prev) => prev.filter((p) => p.property_id !== property_id));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const delProject = async (project_id) => {
    try {
      await api.delete(`/projects/${project_id}`);
      toast.success("Proyecto eliminado");
      setProjects((prev) => prev.filter((p) => p.project_id !== project_id));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setTab = (v) => {
    const n = new URLSearchParams(params);
    if (v === "properties") n.delete("tab"); else n.set("tab", v);
    setParams(n);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-10 pt-8 md:pt-16 pb-16">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="label-eyebrow">{t("nav.dashboard")}</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">{t("dashboard.title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          {tab === "properties" ? (
            <Button asChild className="rounded-full btn-hover-lift" data-testid="new-property-btn">
              <Link to="/dashboard/new"><Plus className="h-4 w-4 mr-2" /> {t("dashboard.new")}</Link>
            </Button>
          ) : (
            <Button asChild className="rounded-full btn-hover-lift" data-testid="new-project-btn">
              <Link to="/dashboard/projects/new"><Plus className="h-4 w-4 mr-2" /> {t("projects.new")}</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-8 flex gap-6 overflow-x-auto" data-testid="dashboard-tabs">
        <button
          onClick={() => setTab("properties")}
          className={`pb-3 text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === "properties" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-properties"
        >
          {t("dash_tabs.properties")} <span className="text-xs opacity-70">({properties.length})</span>
        </button>
        <button
          onClick={() => setTab("projects")}
          className={`pb-3 text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === "projects" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-projects"
        >
          {t("dash_tabs.projects")} <span className="text-xs opacity-70">({projects.length})</span>
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">{t("common.loading")}</div>
      ) : tab === "properties" ? (
        properties.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-border rounded-sm">
            <p className="text-muted-foreground" data-testid="dashboard-empty">{t("dashboard.empty")}</p>
            <Button asChild className="mt-6 rounded-full">
              <Link to="/dashboard/new"><Plus className="h-4 w-4 mr-2" /> {t("dashboard.new")}</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="dashboard-list">
            {properties.map((p) => (
              <div key={p.property_id} className="grid grid-cols-[120px,1fr,auto] gap-4 items-center border border-border p-3 rounded-sm hover:bg-accent/20 transition-colors">
                <div className="aspect-[4/3] overflow-hidden rounded-sm bg-muted">
                  <img src={p.photos?.[0] ? photoUrl(p.photos[0]) : FALLBACK_PROP} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="label-eyebrow">{t(`types.${p.property_type}`)}</div>
                  <h3 className="font-serif text-2xl truncate">{p.title}</h3>
                  <div className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {p.address}</div>
                  <div className="mt-1 font-medium text-primary">{formatPrice(p.price, p.currency)}</div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <Button asChild variant="outline" size="sm" data-testid={`edit-${p.property_id}`}>
                    <Link to={`/dashboard/edit/${p.property_id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.edit")}</Link>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-${p.property_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.delete")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("dashboard.confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{p.title}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("form.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => delProperty(p.property_id)} data-testid={`confirm-delete-${p.property_id}`}>
                          {t("dashboard.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        projects.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-border rounded-sm">
            <p className="text-muted-foreground" data-testid="dashboard-projects-empty">{t("projects.empty")}</p>
            <Button asChild className="mt-6 rounded-full">
              <Link to="/dashboard/projects/new"><Plus className="h-4 w-4 mr-2" /> {t("projects.new")}</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="dashboard-projects-list">
            {projects.map((p) => (
              <div key={p.project_id} className="grid grid-cols-[120px,1fr,auto] gap-4 items-center border border-border p-3 rounded-sm hover:bg-accent/20 transition-colors">
                <div className="aspect-[4/3] overflow-hidden rounded-sm bg-muted">
                  <img src={p.photos?.[0] ? photoUrl(p.photos[0]) : FALLBACK_PROJ} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="label-eyebrow inline-flex items-center gap-1.5">
                    <Building2 className="h-3 w-3" /> {p.developer}
                    {p.confotur && (
                      <span className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm text-[9px] ml-1">
                        <ShieldCheck className="h-2.5 w-2.5" /> Confotur
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl truncate">{p.name}</h3>
                  <div className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {p.address}</div>
                  <div className="mt-1 flex items-center gap-4 text-sm">
                    <span className="text-primary font-medium">
                      {formatPrice(p.price_min, p.currency)} – {formatPrice(p.price_max, p.currency)}
                    </span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {t("projects.months")[p.delivery_month - 1]} {p.delivery_year}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <Button asChild variant="outline" size="sm" data-testid={`edit-project-${p.project_id}`}>
                    <Link to={`/dashboard/projects/edit/${p.project_id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.edit")}</Link>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-project-${p.project_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("dashboard.delete")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("dashboard.confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{p.name}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("form.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => delProject(p.project_id)} data-testid={`confirm-delete-project-${p.project_id}`}>
                          {t("dashboard.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
